import { ColumnDef } from '@tanstack/react-table';
import { DataTableColumnHeader, UserNameCell } from '@/components/core';
import { LeaveOverviewEmployee } from '../../types/leave-overview.types';

interface CreateLeaveOverviewTableColumnsProps {
  t: (key: string) => string;
  /** The employee's email by IAM user id, shown under the name. */
  emailOf?: (userId: string) => string | undefined;
  /** The departments the user may see (`useDepartmentScope().departments`), for the Department
   * column's filter. */
  departments?: readonly { ItemId: string; Title: string }[];
}

/**
 * The filters are the columns' own, but the HR terms listed have no name to filter on: the tab reads
 * these two filters' values itself (the name through an employee search, the department as its id)
 * instead of passing them to the query as they are.
 */
export const createLeaveOverviewTableColumns = ({
  t,
  emailOf,
  departments = [],
}: CreateLeaveOverviewTableColumnsProps): ColumnDef<LeaveOverviewEmployee>[] => [
  {
    id: 'DisplayName',
    // Looked up from the directory per row; the HR term has no name to sort on.
    enableSorting: false,
    // The card has the name as its title and the email in its body instead.
    meta: { mobile: 'hidden', filter: { type: 'text' } },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('EMPLOYEE_NAME')} />,
    accessorFn: (row) => row.DisplayName,
    cell: ({ row }) => (
      <UserNameCell name={row.original.DisplayName} email={emailOf?.(row.original.UserId)} />
    ),
  },
  {
    id: 'CardDisplayName',
    enableSorting: false,
    meta: { mobile: 'title', cardOnly: true },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('EMPLOYEE_NAME')} />,
    accessorFn: (row) => row.DisplayName || '-',
  },
  {
    // Card body, two per line: Email | Department, Allocated | Taken, Requested | Remaining.
    id: 'CardEmail',
    enableSorting: false,
    meta: { mobile: 'body', mobileOrder: 0, cardOnly: true },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('EMAIL')} />,
    accessorFn: (row) => emailOf?.(row.UserId) || '-',
  },
  {
    id: 'DepartmentName',
    meta: {
      mobile: 'body',
      mobileOrder: 1,
      filter: {
        type: 'options',
        dropdown: true,
        options: departments.map((department) => ({
          value: department.ItemId,
          label: department.Title,
        })),
      },
    },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('DEPARTMENT')} />,
    accessorFn: (row) => row.DepartmentName ?? '-',
  },
  // The totals and unlimited categories are summed from the allotments and requests after the page loads.
  {
    id: 'TotalAllocated',
    enableSorting: false,
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('ALLOCATED_LEAVE')} />,
    accessorFn: (row) => row.TotalAllocated,
  },
  {
    id: 'TotalTaken',
    enableSorting: false,
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('TAKEN_LEAVES')} />,
    accessorFn: (row) => row.TotalTaken,
  },
  {
    id: 'TotalRequested',
    enableSorting: false,
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('REQUESTED_LEAVES')} />,
    accessorFn: (row) => row.TotalRequested,
  },
  {
    id: 'TotalRemaining',
    enableSorting: false,
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('REMAINING_LEAVE')} />,
    accessorFn: (row) => row.TotalRemaining,
  },
  {
    id: 'UnlimitedCategoryNames',
    enableSorting: false,
    meta: { mobile: 'full' },
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={t('UNLIMITED_CATEGORIES')} />
    ),
    accessorFn: (row) => row.UnlimitedCategoryNames.join(', ') || '-',
  },
];
